import React, { useEffect, useState } from 'react';
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import { collection, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import AssignUserRoles from './assignUserRoles';
import CreateBrokerUser from './createUserWithPassword';
import CreateAuthenticatedUser from './createAuthenticatedUser';

const AdminPage = () => {
  const auth = getAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [failedNotifications, setFailedNotifications] = useState([]);
  const [isLoadingFailures, setIsLoadingFailures] = useState(false);

  useEffect(() => {
    const checkAdminStatus = async () => {
      const user = auth.currentUser;
      if (user) {
        try {
          const idTokenResult = await user.getIdTokenResult(true);
          setIsAdmin(idTokenResult.claims.admin === true);
        } catch (e) {
          console.error('Error fetching admin claims:', e);
        }
      }
      setLoading(false);
    };

    checkAdminStatus();
  }, [auth]);

  useEffect(() => {
    if (!auth.currentUser || !isAdmin) {
      setFailedNotifications([]);
      return undefined;
    }

    setIsLoadingFailures(true);
    const quotesQuery = query(collection(db, 'assignedAgents'), orderBy('timestamp', 'desc'), limit(200));

    const unsubscribe = onSnapshot(
      quotesQuery,
      (snapshot) => {
        const failures = snapshot.docs
          .map((docSnap) => {
            const data = docSnap.data();
            const notificationStatus = data.notificationStatus || {};
            const customerEmailStatus = notificationStatus.customerEmail || {};
            const agentEmailStatus = notificationStatus.agentEmail || {};

            const customerFailed = customerEmailStatus.attempted && !customerEmailStatus.sent;
            const agentFailed = agentEmailStatus.attempted && !agentEmailStatus.sent;

            if (!customerFailed && !agentFailed) return null;

            const reasons = [];
            if (customerFailed) {
              reasons.push(`Customer email failed: ${customerEmailStatus.error || 'Unknown reason'}`);
            }
            if (agentFailed) {
              reasons.push(`Agent email failed: ${agentEmailStatus.error || 'Unknown reason'}`);
            }

            return {
              id: docSnap.id,
              submittedAt: data.timestamp?.toDate ? data.timestamp.toDate().toLocaleString() : 'Pending timestamp',
              customerName: data.name || 'Unknown customer',
              customerEmail: data.email || 'No email',
              agentName: data.agentName || 'No agent name',
              agentEmail: data.agentEmail || 'No agent email',
              reasons,
            };
          })
          .filter(Boolean);

        setFailedNotifications(failures);
        setIsLoadingFailures(false);
      },
      (snapshotError) => {
        console.error('Error loading failed notification report:', snapshotError);
        setIsLoadingFailures(false);
      }
    );

    return () => unsubscribe();
  }, [auth, isAdmin]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(''); // Reset any previous error messages

    try {
      await signInWithEmailAndPassword(auth, email, password);
      const idTokenResult = await auth.currentUser.getIdTokenResult(true);
      setIsAdmin(idTokenResult.claims.admin === true);

      if (!idTokenResult.claims.admin) {
        setError('You do not have admin rights.');
      }
    } catch (error) {
      setError('Failed to login. Please check your credentials.');
      console.error('Login error:', error);
    }
  };

  if (loading) {
    return <p>Loading...</p>;
  }

  return (
    <div>
      <h1>Admin Page</h1>
      {auth.currentUser && isAdmin ? (
        <div>
          <p>Welcome, Admin! Here are your controls.</p>
          <br></br>
          <ol>
            <li>everything runs off of the Firebase Authentication database UID.</li>
            <li>If user is not authenticated, Create Authenticated User.</li>
            <li>also need to add the agent to michiganagentlist.</li>
            <li>then using their uid from the auth database, assign their user role to Broker Login using Assign User Roles. </li>
            <li>Then, in the userRoles collection, set their temporaryPassword to true.  We can automate this at some point. </li>
            <li>Provide agent their temp password.  they will prompted to reset it upon logging in.</li>
            <li>if a user is already authenticated you just need to assign them the userRole and set their temporaryPassword to false, unless you want them to create a new password.  probably should.</li>
          </ol>

          <CreateAuthenticatedUser />
          <AssignUserRoles />
          <CreateBrokerUser />

          <hr />
          <h2>Quote Email Delivery Failures</h2>
          <p>Shows recent quotes where customer or agent email notifications did not send.</p>
          {isLoadingFailures ? (
            <p>Loading failed notification report...</p>
          ) : failedNotifications.length === 0 ? (
            <p>No failed quote email notifications found in the last 200 submissions.</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'left' }}>Submitted</th>
                  <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'left' }}>Customer</th>
                  <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'left' }}>Agent</th>
                  <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'left' }}>Failure Reason</th>
                </tr>
              </thead>
              <tbody>
                {failedNotifications.map((item) => (
                  <tr key={item.id}>
                    <td style={{ border: '1px solid #ccc', padding: '8px' }}>{item.submittedAt}</td>
                    <td style={{ border: '1px solid #ccc', padding: '8px' }}>
                      <div>{item.customerName}</div>
                      <div>{item.customerEmail}</div>
                    </td>
                    <td style={{ border: '1px solid #ccc', padding: '8px' }}>
                      <div>{item.agentName}</div>
                      <div>{item.agentEmail}</div>
                    </td>
                    <td style={{ border: '1px solid #ccc', padding: '8px' }}>{item.reasons.join(' | ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        <div>
          <h2>Admin Login</h2>
          {error && <p style={{ color: 'red' }}>{error}</p>}
          <form onSubmit={handleLogin}>
            <div>
              <label>Email:</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div>
              <label>Password:</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <button type="submit">Login</button>
          </form>
          <p>You must log in with admin credentials to access this area.</p>
        </div>
      )}
    </div>
  );
};

export default AdminPage;

/* WORKFLOW FOR ADDING AGENTS:
1:  everything runs off of the Firebase Authentication database UID.
2. If user is not authenticated, Create Authenticated User.
3. also need to add the agent to michiganagentlist.
4. then using their uid from the auth database, assign their user role to Broker Login using Assign User Roles. 
5.  Then, in the userRoles collection, set their temporaryPassword to true.  We can automate this at some point. 
6. Provide agent their temp password.  they will prompted to reset it upon logging in.  
7. if a user is already authenticated you just need to assign them the userRole and set their temporaryPassword to false, unless you want them to create a new password.  probably should.
*/